import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import type {Repository} from '@github-ui/current-repository'
import {repositoryTreePath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {ScreenReaderHeading} from '@github-ui/screen-reader-heading'
import {Heading, Link as PrimerLink} from '@primer/react'
import {useMemo} from 'react'
import styles from './Breadcrumb.module.css'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'

const separatorCharacter = '/'
type BreadcrumbSize = 'lg' | 'md'

interface BreadcrumbProps {
  id?: string
  fileNameId?: string
  commitish: string
  path: string
  repo: Repository
  isFolder: boolean
  size?: BreadcrumbSize
  showCopyPathButton?: boolean
}

export function Breadcrumb({
  id = 'breadcrumb',
  fileNameId,
  path,
  repo,
  commitish,
  isFolder,
  size = 'lg',
  showCopyPathButton,
}: BreadcrumbProps) {
  const {fileName, segments} = useMemo(() => getPathSegmentData(path), [path])
  const isRoot = !path

  // if viewing the root of a repo as a tree directory, use the repo name as the h1
  // this typically only happens when being redirected here after creating a new file at the root of a repo
  const useRepoNameAsH1 = isFolder && path === '/'

  const sizeClass = size === 'md' ? styles.md : styles.lg

  return (
    <div className={`${styles.container} ${sizeClass}`}>
      <nav data-testid="breadcrumbs" aria-labelledby={`${id}-heading`} id={id} className={styles.nav}>
        <ScreenReaderHeading id={`${id}-heading`} as="h2" text="Breadcrumbs" />

        <ol className={styles.list}>
          <li className={styles.listItem}>
            <RepoLink repo={repo} commitish={commitish} />
            {useRepoNameAsH1 && <ScreenReaderHeading as="h1" text={repo.name} />}
          </li>
          {segments.map(({directoryName, directoryPath}) => (
            <li className={styles.listItem} key={directoryPath}>
              <Separator size={size} />
              {directoryName ? (
                <DirectoryLink path={directoryPath} directoryName={directoryName} repo={repo} commitish={commitish} />
              ) : null}
            </li>
          ))}
        </ol>
      </nav>
      {fileName && (
        <div data-testid="breadcrumbs-filename" className={styles.filename} key={fileName}>
          <Separator size={size} />

          <FileName value={fileName} id={fileNameId} size={size} />

          {!isRoot && isFolder && <Separator size={size} />}
        </div>
      )}
      {showCopyPathButton && (
        <CopyToClipboardButton
          ariaLabel="Copy path"
          textToCopy={path}
          tooltipProps={{direction: 'nw'}}
          size="small"
          className="ml-2"
        />
      )}
    </div>
  )
}

function RepoLink({repo, commitish}: {repo: Repository; commitish: string}) {
  const isDataRouterEnabled = useIsDataRouterEnabled()
  return (
    <PrimerLink
      as={Link}
      className={styles.repoLink}
      to={repositoryTreePath({repo, commitish, action: 'tree'})}
      data-testid="breadcrumbs-repo-link"
      reloadDocument={!isDataRouterEnabled}
    >
      {repo.name}
    </PrimerLink>
  )
}
interface DirectoryLinkProps {
  commitish: string
  directoryName: string
  path: string
  repo: Repository
}

function DirectoryLink({directoryName, path, repo, commitish}: DirectoryLinkProps) {
  return (
    <PrimerLink
      as={Link}
      to={repositoryTreePath({repo, commitish, path, action: 'tree'})}
      className={styles.directoryLink}
    >
      {directoryName}
    </PrimerLink>
  )
}

export function Separator({size = 'lg'}: {size?: BreadcrumbSize}) {
  const sizeClass = size === 'md' ? styles.md : styles.lg

  return (
    <span className={`${styles.separator} ${sizeClass}`} aria-hidden="true">
      /
    </span>
  )
}

function FileName({value, id, size = 'lg'}: {value: string; id?: string; size?: BreadcrumbSize}) {
  const sizeClass = size === 'md' ? styles.md : styles.lg

  return (
    <Heading as="h1" tabIndex={-1} className={`${styles.filenameHeading} ${sizeClass}`} id={id}>
      {value}
    </Heading>
  )
}

function getPathSegmentData(path: string) {
  const segments = path.split(separatorCharacter)
  const fileName = segments.pop() || ''

  return {
    fileName,
    segments: segments.map((segment, i) => ({
      directoryName: segment,
      directoryPath: segments.slice(0, i + 1).join(separatorCharacter),
    })),
  }
}
